import { create } from 'zustand';
import { apiFetch } from '@/lib/api';
import type { Location } from '@/lib/types';

export type VisitStatus =
  | 'pending'
  | 'accepted'
  | 'in_progress'
  | 'submitted'
  | 'processing'
  | 'processed'
  | 'approved'
  | 'rejected'
  | 'signed'
  | 'paid'
  | 'finished'
  | 'cancelled'
  | 'failed';

export interface VisitExecutor {
  id: string;
  /** ФИО из full_name воркера; null — поле не заполнено, показываем login */
  name: string | null;
  login: string;
  phone: string | null;
  /** Внутренний ID исполнителя у клиента (workers.internal_id) */
  internalId: string | null;
}

export interface VisitTemplateRef {
  id: string;
  title: string;
  slug: string;
}

export interface Visit {
  id: string;
  visitTemplateId: string;
  userId: string;
  status: VisitStatus;
  locationId: string;
  location: Location | null;
  locationLat: string | null;
  locationLng: string | null;
  plannedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  processedAt: string | null;
  isPaid: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
  pdfUrl: string | null;
  signedAt: string | null;
  signerId: string | null;
  signerName: string | null;
  signatureId: string | null;
  createdAt: string;
  updatedAt: string;
  user: VisitExecutor;
  template: VisitTemplateRef;
}

interface VisitsResponse {
  items: Visit[];
  total: number;
  page: number;
  limit: number;
}

export interface VisitsQuery {
  page?: number;
  limit?: number;
  status?: string | null;
}

interface VisitsState {
  items: Visit[];
  total: number;
  page: number;
  limit: number;
  statusFilter: string | null;
  isLoading: boolean;
  error: string | null;
  fetchVisits: (query?: VisitsQuery) => Promise<void>;
  setPage: (page: number) => void;
  setStatusFilter: (status: string | null) => void;
}

const DEFAULT_LIMIT = 15;

export const useVisitsStore = create<VisitsState>()((set, get) => ({
  items: [],
  total: 0,
  page: 1,
  limit: DEFAULT_LIMIT,
  statusFilter: null,
  isLoading: false,
  error: null,

  fetchVisits: async (query) => {
    const { page, limit, statusFilter } = get();
    const params = new URLSearchParams();
    params.set('page', String(query?.page ?? page));
    params.set('limit', String(query?.limit ?? limit));
    const status = query?.status !== undefined ? query.status : statusFilter;
    if (status) params.set('status', status);

    set({ isLoading: true, error: null });
    try {
      const res = await apiFetch(`/api/v1/admin/visits/?${params.toString()}`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const data = (await res.json()) as VisitsResponse;
      set({
        items: data.items,
        total: data.total,
        page: data.page,
        limit: data.limit,
        isLoading: false,
      });
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Failed to load visits' });
    }
  },

  setPage: (page) => {
    set({ page });
    void get().fetchVisits({ page });
  },

  setStatusFilter: (status) => {
    set({ statusFilter: status, page: 1 });
    void get().fetchVisits({ page: 1, status });
  },
}));

export const selectVisitsItems = (s: VisitsState) => s.items;
export const selectVisitsTotal = (s: VisitsState) => s.total;
export const selectVisitsPage = (s: VisitsState) => s.page;
export const selectVisitsLimit = (s: VisitsState) => s.limit;
export const selectVisitsIsLoading = (s: VisitsState) => s.isLoading;
export const selectVisitsError = (s: VisitsState) => s.error;
export const selectVisitsStatusFilter = (s: VisitsState) => s.statusFilter;
